import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { assetsAPI } from '../../services/api';
import { useFormValidation, FieldError } from '../../hooks/useFormValidation';
import toast from 'react-hot-toast';

const FIELD = ({ label, children, required }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}{required && <span className="text-danger ml-0.5">*</span>}</label>
    {children}
  </div>
);

const INPUT = (props) => (
  <input {...props} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
);

const SELECT = ({ children, ...props }) => (
  <select {...props} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30 bg-white">
    {children}
  </select>
);

export default function AssetModal({ asset, onClose, onSaved }) {
  const isEdit = !!asset;
  const [loading, setLoading] = useState(false);
  const { errors, validate, clearError } = useFormValidation({
    asset_code: (v) => !v?.trim() ? 'Asset code is required' : null,
    name:       (v) => !v?.trim() ? 'Asset name is required' : null,
    asset_type: (v) => !v        ? 'Asset type is required'  : null,
  });
  const [form, setForm] = useState({
    asset_code: '', name: '', asset_type: 'solar', status: 'operational',
    location_name: '', country: '', latitude: '', longitude: '',
    capacity_mw: '', install_date: '', warranty_expiry: '',
    manufacturer: '', serial_number: '', model_number: '',
    purchase_price: '', depreciation_years: '20', salvage_value: '0', notes: '',
  });

  useEffect(() => {
    if (asset) {
      setForm({
        asset_code:        asset.asset_code        || '',
        name:              asset.name              || '',
        asset_type:        asset.asset_type        || 'solar',
        status:            asset.status            || 'operational',
        location_name:     asset.location_name     || '',
        country:           asset.country           || '',
        latitude:          asset.latitude          || '',
        longitude:         asset.longitude         || '',
        capacity_mw:       asset.capacity_mw       || '',
        install_date:      asset.install_date      ? asset.install_date.split('T')[0] : '',
        warranty_expiry:   asset.warranty_expiry   ? asset.warranty_expiry.split('T')[0] : '',
        manufacturer:      asset.manufacturer      || '',
        serial_number:     asset.serial_number     || '',
        model_number:      asset.model_number      || '',
        purchase_price:    asset.purchase_price    || '',
        depreciation_years:asset.depreciation_years|| '20',
        salvage_value:     asset.salvage_value     || '0',
        notes:             asset.notes             || '',
      });
    }
  }, [asset]);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate(form)) return;
    setLoading(true);
    try {
      const payload = { ...form };
      // Convert empty strings to null for numeric fields
      ['latitude','longitude','capacity_mw','purchase_price','depreciation_years','salvage_value'].forEach(k => {
        if (payload[k] === '') payload[k] = null;
      });
      ['install_date','warranty_expiry'].forEach(k => {
        if (payload[k] === '') payload[k] = null;
      });

      if (isEdit) {
        await assetsAPI.update(asset.id, payload);
        toast.success('Asset updated');
      } else {
        await assetsAPI.create(payload);
        toast.success('Asset created');
      }
      onSaved();
    } catch {}
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-base font-semibold text-gray-900">{isEdit ? 'Edit Asset' : 'Add New Asset'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Section: Basic Info */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Basic Information</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FIELD label="Asset Code" required>
                <INPUT value={form.asset_code} onChange={e=>{set('asset_code')(e);clearError('asset_code');}} placeholder="e.g. SOL-001"/>
              <FieldError error={errors.asset_code}/>
              </FIELD>
              <FIELD label="Asset Name" required>
                <INPUT value={form.name} onChange={e=>{set('name')(e);clearError('name');}} placeholder="e.g. North Solar Farm"/>
              <FieldError error={errors.name}/>
              </FIELD>
              <FIELD label="Asset Type" required>
                <SELECT value={form.asset_type} onChange={set('asset_type')}>
                  {['solar','wind','hydro','grid','battery','other'].map(t => (
                    <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>
                  ))}
                </SELECT>
              </FIELD>
              <FIELD label="Status" required>
                <SELECT value={form.status} onChange={set('status')}>
                  <option value="operational">Operational</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="offline">Offline</option>
                  <option value="decommissioned">Decommissioned</option>
                </SELECT>
              </FIELD>
              <FIELD label="Capacity (MW)">
                <INPUT type="number" step="0.0001" value={form.capacity_mw} onChange={set('capacity_mw')} placeholder="e.g. 50.5" />
              </FIELD>
            </div>
          </div>

          {/* Section: Location */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Location</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FIELD label="Location Name">
                <INPUT value={form.location_name} onChange={set('location_name')} placeholder="e.g. Bavaria, Germany" />
              </FIELD>
              <FIELD label="Country">
                <INPUT value={form.country} onChange={set('country')} placeholder="e.g. DE" />
              </FIELD>
              <FIELD label="Latitude">
                <INPUT type="number" step="any" value={form.latitude} onChange={set('latitude')} placeholder="48.137154" />
              </FIELD>
              <FIELD label="Longitude">
                <INPUT type="number" step="any" value={form.longitude} onChange={set('longitude')} placeholder="11.576124" />
              </FIELD>
            </div>
          </div>

          {/* Section: Technical */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Technical Details</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FIELD label="Manufacturer">
                <INPUT value={form.manufacturer} onChange={set('manufacturer')} placeholder="e.g. Siemens Gamesa" />
              </FIELD>
              <FIELD label="Model Number">
                <INPUT value={form.model_number} onChange={set('model_number')} placeholder="e.g. SG 5.0-145" />
              </FIELD>
              <FIELD label="Serial Number">
                <INPUT value={form.serial_number} onChange={set('serial_number')} placeholder="e.g. SN-2024-0042" />
              </FIELD>
              <FIELD label="Install Date">
                <INPUT type="date" value={form.install_date} onChange={set('install_date')} />
              </FIELD>
              <FIELD label="Warranty Expiry">
                <INPUT type="date" value={form.warranty_expiry} onChange={set('warranty_expiry')} />
              </FIELD>
            </div>
          </div>

          {/* Section: Financial */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Financial</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FIELD label="Purchase Price (€)">
                <INPUT type="number" step="0.01" value={form.purchase_price} onChange={set('purchase_price')} placeholder="0.00" />
              </FIELD>
              <FIELD label="Depreciation Years">
                <INPUT type="number" value={form.depreciation_years} onChange={set('depreciation_years')} placeholder="20" />
              </FIELD>
              <FIELD label="Salvage Value (€)">
                <INPUT type="number" step="0.01" value={form.salvage_value} onChange={set('salvage_value')} placeholder="0.00" />
              </FIELD>
            </div>
          </div>

          {/* Notes */}
          <FIELD label="Notes">
            <textarea value={form.notes} onChange={set('notes')} rows={3} placeholder="Any additional notes…"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30 resize-none" />
          </FIELD>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
          <button onClick={handleSubmit} disabled={loading}
            className="flex items-center gap-2 px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-60">
            {loading && <Loader2 size={14} className="animate-spin" />}
            {isEdit ? 'Save Changes' : 'Create Asset'}
          </button>
        </div>
      </div>
    </div>
  );
}
